import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MetricCard } from "@/components/Reports/MetricCard";
import { HelpTooltip } from "@/components/HelpTooltip";
import { Wallet, Users, TrendingUp, Award, ChevronLeft, AlertTriangle, Zap, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function UserBalancesReport({
  metrics = {},
  topUsers = [],
  inactiveUsers = [],
  boostedUsers = [],
  balanceDistribution = []
}) {
  const handleBackToReports = () => {
    router.visit("/admin?tab=reports");
  };

  const handleExport = async () => {
    try {
      const url = `/admin/reports/user_balances/export`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_balances_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getInactiveStatus = (days) => {
    if (days >= 180) return { label: "CRÍTICO", color: "bg-red-100 text-red-700 border-red-200" };
    if (days >= 120) return { label: "MUY ALTO", color: "bg-orange-100 text-orange-700 border-orange-200" };
    if (days >= 90) return { label: "ALTO", color: "bg-yellow-100 text-yellow-700 border-yellow-200" };
    return { label: "MODERADO", color: "bg-gray-100 text-gray-600 border-gray-200" };
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToReports}
              className="text-purple-600 hover:bg-purple-50 -ml-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver a Reportes
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-bambu-black flex items-center gap-3">
            <Wallet className="w-8 h-8 text-emerald-600" />
            Reporte de Balance Individual
            <HelpTooltip content={
              <div>
                <p className="font-semibold mb-1">¿Qué es este reporte?</p>
                <p className="mb-2">Monitorea el estado de saldos de puntos de todos los usuarios, identifica usuarios inactivos y analiza distribución de riqueza.</p>
                <p className="font-semibold mb-1">Verás:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Top 20 usuarios con mayor saldo</li>
                  <li>Usuarios inactivos (90+ días)</li>
                  <li>Usuarios con boost activo</li>
                  <li>Distribución de balances por rangos</li>
                  <li>Promedio y mediana de puntos</li>
                </ul>
              </div>
            } />
          </h1>
          <p className="text-gray-500 mt-1">Detalle de puntos y actividad por usuario</p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
        >
          <Wallet className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Usuarios Activos"
          value={metrics.total_users || 0}
          icon={Users}
          color="blue"
          helpText="Número total de usuarios con is_active: true en el sistema"
        />
        <MetricCard
          title="Balance Total"
          value={metrics.total_balance || 0}
          icon={Wallet}
          color="green"
          suffix=" pts"
          helpText="Suma de todos los points_balance del sistema. Representa el pasivo total de puntos por canjear"
        />
        <MetricCard
          title="Promedio por Usuario"
          value={metrics.avg_balance || 0}
          icon={TrendingUp}
          color="purple"
          suffix=" pts"
          helpText="Balance promedio de puntos por usuario activo"
        />
        <MetricCard
          title="Usuarios Inactivos"
          value={metrics.inactive_users_count || 0}
          icon={AlertTriangle}
          color="orange"
          helpText="Usuarios activos que no han enviado reconocimientos en los últimos 90 días"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Balance Mediano</p>
                <p className="text-2xl font-bold text-gray-800">{metrics.median_balance || 0} pts</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Usuarios con Boost</p>
                <p className="text-2xl font-bold text-gray-800">{metrics.users_with_boost || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{metrics.total_boost || 0} pts total</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Rango de Balances</p>
                <p className="text-lg font-bold text-gray-800">
                  {metrics.min_balance || 0} - {metrics.max_balance || 0} pts
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Distribución de Balances</CardTitle>
          <p className="text-sm text-gray-500">Cantidad de usuarios por rango de puntos</p>
        </CardHeader>
        <CardContent>
          {balanceDistribution.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              No hay datos para mostrar
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={balanceDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="range" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} name="Usuarios" />
              </RechartsBarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {boostedUsers.length > 0 && (
        <Card className="border-2 border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              Usuarios con Allowance Boost
            </CardTitle>
            <p className="text-sm text-gray-500">Usuarios que tienen boost temporal de presupuesto</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-amber-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuario</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Departamento</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Boost</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Allowance Base</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {boostedUsers.map((user) => (
                    <tr key={user.id} className="border-b border-amber-100 hover:bg-amber-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800">{user.full_name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        {user.department?.name}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-amber-100 text-amber-700 font-bold">
                          +{user.allowance_boost} pts
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600">{user.monthly_allowance} pts</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                        {user.points_balance}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Top 20 Usuarios por Balance
          </CardTitle>
          <p className="text-sm text-gray-500">Usuarios con mayor saldo de puntos acumulados</p>
        </CardHeader>
        <CardContent>
          {topUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No hay datos disponibles</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Ranking</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuario</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Departamento</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Recibidos</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Actividad</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map((user, index) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                          index === 0 ? "bg-yellow-100 text-yellow-700" :
                          index === 1 ? "bg-gray-100 text-gray-600" :
                          index === 2 ? "bg-orange-100 text-orange-700" :
                          "bg-blue-50 text-blue-600"
                        )}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800">{user.full_name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        {user.department?.name}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="font-medium text-gray-700">{user.received_count}</div>
                        <div className="text-xs text-gray-500">{user.total_received} pts</div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {user.days_since_activity >= 90 ? (
                          <Badge className="bg-orange-100 text-orange-700 text-xs">
                            {user.days_since_activity}d
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-600">{user.days_since_activity}d</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="font-mono font-bold text-emerald-600 text-base">
                          {user.points_balance}
                        </div>
                        {user.allowance_boost > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-1">
                            +{user.allowance_boost}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-orange-200 bg-orange-50/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Usuarios Inactivos ({inactiveUsers.length})
          </CardTitle>
          <p className="text-sm text-gray-500">Usuarios sin actividad (reconocimientos enviados) en más de 90 días</p>
        </CardHeader>
        <CardContent>
          {inactiveUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              ¡Excelente! No hay usuarios inactivos
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-orange-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuario</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Departamento</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Balance</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Última Actividad</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Días Inactivo</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveUsers.map((user) => {
                    const status = getInactiveStatus(user.days_inactive);
                    return (
                      <tr key={user.id} className="border-b border-orange-100 hover:bg-orange-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-800">{user.full_name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-700">
                        {user.department?.name}
                      </td>
                        <td className="py-3 px-4 text-center font-mono font-medium text-gray-800">
                          {user.points_balance}
                        </td>
                        <td className="py-3 px-4 text-center text-xs text-gray-600">
                          {formatDate(user.last_sent_at)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-orange-700">{user.days_inactive}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={cn("text-xs font-bold", status.color)}>
                            {status.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

UserBalancesReport.layout = page => <AppLayout children={page} />;
